import type { AttributeRule, FlagRing, FlagRingServe, FlagStep, FlagStepGate } from 'definitions';
import {
  FLAG_GUARDRAIL_MAX_MARGIN_BP,
  MAX_FLAG_RING_ID_LENGTH,
  MAX_FLAG_RING_IDS,
  MAX_FLAG_RINGS,
  MAX_FLAG_STEP_DURATION_MS,
  MAX_FLAG_STEPS,
} from 'definitions';

/*
 * What the flag page's editors hold while someone types, and the conversion to and from what
 * PUT /flags/:id/plan accepts. The limits mirror servers/api/flags/validate-plan.ts so Save stays
 * disabled, with the reason beside it, until the API would accept the body. The server stays the
 * judge.
 */

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

/** The most a holdback may keep Off (MAX_FLAG_HOLDBACK_BP on the server). */
export const MAX_HOLDBACK_PERCENT = 50;

export type DurationUnit = 'hours' | 'days';

export type StepDraft = {
  /** Percent as typed, "25" or "12.5". */
  percent: string;
  /** Minimum time on this step, as typed, in `unit`. Empty for none. */
  duration: string;
  unit: DurationUnit;
  /** A datetime-local value, "2026-11-02T09:00", or empty for none. */
  notBefore: string;
  /**
   * The date field holds a half-typed value: the input reports it as empty, which must not be
   * read as "no date" and drop the one the step had.
   */
  notBeforeIncomplete?: boolean;
  /**
   * What lets the step move on (FlagStep.gate). A 'kpi' step keeps its typed time, so
   * switching back restores it, but the time is not saved: the guardrail decides instead.
   */
  gate: FlagStepGate;
};

export type Parsed<T> = { ok: true; value: T } | { ok: false; reason: string };

const pad = (value: number) => String(value).padStart(2, '0');

/** Epoch ms as a datetime-local value in the browser's time zone. */
export function toLocalInput(ms: number): string {
  const date = new Date(ms);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** A datetime-local value back to epoch ms, or null for an empty or unreadable one. */
export function fromLocalInput(value: string): number | null {
  if (!value.trim()) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * A schedule time while it is edited. null is untouched; 'clear' is the field's Clear button;
 * otherwise the input's value. A datetime-local reports '' while a date is half typed (Chrome
 * does for every incomplete field), so '' is never read as "remove the schedule": only Clear is.
 */
export type TimeDraft = null | 'clear' | { text: string };

/**
 * What a schedule field sends to PUT /flags/:id/schedule: undefined to leave it, null to clear
 * it, or epoch ms. A half typed or unreadable date is refused, so Save cannot clear a schedule
 * by accident.
 */
export function parseTimeDraft(draft: TimeDraft): Parsed<number | null | undefined> {
  if (draft === null) return { ok: true, value: undefined };
  if (draft === 'clear') return { ok: true, value: null };
  const ms = fromLocalInput(draft.text);
  return ms === null ? { ok: false, reason: 'incomplete' } : { ok: true, value: ms };
}

export function stepToDraft(step: FlagStep): StepDraft {
  const ms = step.minDurationMs ?? 0;
  const inDays = ms > 0 && ms % DAY === 0;
  return {
    percent: String(step.rolloutBp / 100),
    duration: ms > 0 ? String(inDays ? ms / DAY : Number((ms / HOUR).toFixed(2))) : '',
    unit: inDays ? 'days' : 'hours',
    notBefore: step.notBefore ? toLocalInput(step.notBefore) : '',
    gate: step.gate ?? 'time',
  };
}

/** The step a new row starts as: double the last share, a day at it, gated as the last one is. */
export function nextStepDraft(steps: readonly StepDraft[]): StepDraft {
  const lastStep = steps[steps.length - 1];
  const last = Number(lastStep?.percent);
  const percent = Number.isFinite(last) && last > 0 ? Math.min(100, last * 2) : 10;
  return { percent: String(percent), duration: '1', unit: 'days', notBefore: '', gate: lastStep?.gate ?? 'time' };
}

/** Whether a step's minimum time gates it: a 'kpi' step waits on the guardrail instead. */
export const usesStepTime = (gate: FlagStepGate) => gate !== 'kpi';

/** Whether a step waits on the guardrail. */
export const usesGuardrail = (gate: FlagStepGate) => gate !== 'time';

/** A percent as typed to basis points, or null when it is not 0 to 100 in steps of 0.01. */
export function percentToBp(text: string): number | null {
  if (!text.trim()) return null;
  const value = Number(text.trim());
  if (!Number.isFinite(value) || value < 0 || value > 100) return null;
  const bp = Math.round(value * 100);
  return Math.abs(bp - value * 100) < 1e-6 ? bp : null;
}

/** The steps for PUT /flags/:id/plan, or the first problem as a sentence. */
export function parseStepDrafts(drafts: readonly StepDraft[]): Parsed<FlagStep[]> {
  if (drafts.length > MAX_FLAG_STEPS) return { ok: false, reason: `A plan has at most ${MAX_FLAG_STEPS} steps.` };
  const steps: FlagStep[] = [];
  for (const [index, draft] of drafts.entries()) {
    const where = `Step ${index + 1}`;
    const rolloutBp = percentToBp(draft.percent);
    if (rolloutBp === null) return { ok: false, reason: `${where}: enter a share from 0 to 100.` };
    const previous = steps[index - 1];
    if (previous && rolloutBp < previous.rolloutBp) {
      return { ok: false, reason: `${where} rolls out less than step ${index}. A plan only widens.` };
    }

    const step: FlagStep = { rolloutBp, gate: draft.gate };
    // A 'kpi' step's typed time is kept on the page but not saved: nothing reads it.
    if (usesStepTime(draft.gate) && draft.duration.trim()) {
      const amount = Number(draft.duration.trim());
      const ms = Math.round(amount * (draft.unit === 'days' ? DAY : HOUR));
      if (!Number.isFinite(amount) || amount < 0 || ms > MAX_FLAG_STEP_DURATION_MS) {
        return { ok: false, reason: `${where}: the time on it is at most a year.` };
      }
      if (ms > 0) step.minDurationMs = ms;
    }
    if (draft.notBeforeIncomplete) {
      return { ok: false, reason: `${where}: finish the date, or empty the field for none.` };
    }
    if (draft.notBefore.trim()) {
      const notBefore = fromLocalInput(draft.notBefore);
      if (notBefore === null) return { ok: false, reason: `${where}: the date is not readable.` };
      step.notBefore = notBefore;
    }
    steps.push(step);
  }
  return { ok: true, value: steps };
}

/** The guardrail margin, as a percent (FLAG_GUARDRAIL_DEFAULT_MARGIN_BP, 200, is "2"). */
export const marginToText = (bp: number) => String(bp / 100);

/**
 * A guardrail margin as typed, a relative percent, to basis points: 0.01 to 50 in hundredths,
 * the 1 to 5000 bp validate-plan.ts accepts. Null for anything else.
 */
export function parseMarginPercent(text: string): number | null {
  const bp = percentToBp(text);
  return bp === null || bp < 1 || bp > FLAG_GUARDRAIL_MAX_MARGIN_BP ? null : bp;
}

/** JSON with object keys sorted, so two values compare equal whatever order the server wrote them in. */
export function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, item]) => item !== undefined)
      .sort(([a], [b]) => a.localeCompare(b));
    return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${stableJson(item)}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

/** True when the drafts say the same as the saved steps. */
export const sameSteps = (a: readonly FlagStep[], b: readonly FlagStep[]) => stableJson(a) === stableJson(b);

export type RingDraft = {
  serve: FlagRingServe;
  rules: AttributeRule[];
  /** IDs as typed: one per line, or separated by commas or spaces. */
  idsText: string;
};

export const ringToDraft = (ring: FlagRing): RingDraft => ({
  serve: ring.serve,
  rules: ring.rules,
  idsText: (ring.ids ?? []).join('\n'),
});

export const EMPTY_RING: RingDraft = { serve: 'on', rules: [], idsText: '' };

/** The IDs in a ring's text, trimmed and without repeats. */
export const ringIds = (text: string) => [...new Set(text.split(/[\s,]+/).filter(Boolean))];

/** The rings for PUT /flags/:id/plan, or the first problem as a sentence. */
export function parseRingDrafts(drafts: readonly RingDraft[]): Parsed<FlagRing[]> {
  if (drafts.length > MAX_FLAG_RINGS) return { ok: false, reason: `A flag has at most ${MAX_FLAG_RINGS} rings.` };
  const rings: FlagRing[] = [];
  for (const [index, draft] of drafts.entries()) {
    const where = `Ring ${index + 1}`;
    const ids = ringIds(draft.idsText);
    if (ids.length > MAX_FLAG_RING_IDS) return { ok: false, reason: `${where}: at most ${MAX_FLAG_RING_IDS} IDs.` };
    if (ids.some((id) => id.length > MAX_FLAG_RING_ID_LENGTH)) {
      return { ok: false, reason: `${where}: an ID is at most ${MAX_FLAG_RING_ID_LENGTH} characters.` };
    }
    if (draft.rules.length === 0 && ids.length === 0) return { ok: false, reason: `${where} needs a rule or an ID.` };
    rings.push(
      ids.length > 0 ? { rules: draft.rules, ids, serve: draft.serve } : { rules: draft.rules, serve: draft.serve },
    );
  }
  return { ok: true, value: rings };
}

export const sameRings = (a: readonly FlagRing[], b: readonly FlagRing[]) => stableJson(a) === stableJson(b);

/**
 * A flag key from its name: lowercase, accents dropped, anything else a hyphen, at most 64.
 * Matches FLAG_KEY_PATTERN for any name with a letter or digit in it.
 */
export function suggestFlagKey(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, 64)
    .replace(/-+$/, '');
}
